import type { Component, JSX } from 'solid-js';
import { createEffect, Show } from 'solid-js';
import { Router, useLocation, useBeforeLeave } from '@solidjs/router';
import { routesFor } from './routes';
import { SearchBridge } from './components/SearchBridge';
import { FullscreenPrompt } from './components/FullscreenPrompt';
import { BootstrapProvider } from './state/bootstrap';
import { refreshKey } from './state/appRefresh';
import { pageKey, sharesHero, slidesBack, type SlideDirection } from './navigation/transitions';
import { recordPageChange } from './navigation/returnTarget';
import { getScrollTop, scrollToTop } from './utils/scroll';
import { trackPageView } from './pageMeta';
import type { Bootstrap } from './types';

const OffCanvasCloser: Component = () => {
    const loc = useLocation();
    createEffect(() => {
        // touch the path so the effect re-runs on every navigation
        void loc.pathname;
        const wrapper = document.querySelector('.off-canvas-wrapper');
        if (wrapper) wrapper.classList.remove('active');
        document.body.classList.remove('fix');
    });
    return null;
};

const PageViewTracker: Component = () => {
    const loc = useLocation();
    let firstFire = true;
    createEffect(() => {
        const path = loc.pathname;
        const search = loc.search;
        // Defer slightly so the new page has a chance to set document.title
        // before we send the event. The first fire (initial mount) is also
        // delayed so SSR-rendered title is captured rather than the brief
        // pre-mount value.
        const wait = firstFire ? 0 : 200;
        firstFire = false;
        window.setTimeout(() => trackPageView(path, search), wait);
    });
    return null;
};

// Start a freshly mounted page at the top. The router's own scroll-to-top calls
// `window.scrollTo(0, 0)`, which is a no-op here: `html, body { height: 100% }`
// (modern.css) makes `body` the scroll root, so without this a page opened from
// the menu inherits the previous page's scroll offset. Returns to a list still
// end on the album just left: useReturnScroll pins that on the next frame,
// before paint. The outgoing ghost is shifted up by the old offset so it keeps
// sliding out exactly where it was on screen.
const scrollNewPageToTop = (viewport: HTMLElement | undefined): void => {
    const y = getScrollTop();
    if (!y) return;
    viewport
        ?.querySelectorAll<HTMLElement>(':scope > .page-layer--leave, :scope > .page-layer--leave-back')
        .forEach((ghost) => {
            ghost.style.top = `${-y}px`;
        });
    scrollToTop();
};

// Slide direction for the next page layer to mount, armed by a link click
// (useBeforeLeave) or browser back/forward (popstate). A plain variable, not a
// signal: it's read *and consumed* by the mount, so an already-visible layer is
// never retroactively animated. Consuming at mount also fixes redirects
// (`/portfolio` -> `<Navigate>` -> `/2026`): if the intermediate page never
// commits, the real destination inherits the pending slide (one clean swipe);
// if it does commit, it animates once and the destination then swaps in —
// never twice.
let pendingSlide: SlideDirection | null = null;
// Whether that slide keeps the header still (sharesHero): only the content
// below the `.page-static` slides, in both the ghost and the incoming layer.
let pendingKeepHero = false;

// Keep the outgoing page on screen during the slide: deep-clone its layer into
// an absolutely-positioned "ghost" that animates out while the fresh page
// animates in, then removes itself. With `keepHero` the ghost's header is
// hidden and only its content slides out, over the incoming page's header.
const spawnGhost = (viewport: HTMLElement, layer: HTMLElement, back: boolean, keepHero: boolean): void => {
    const ghost = layer.cloneNode(true) as HTMLElement;
    ghost.classList.remove('page-layer--enter', 'page-layer--enter-back', 'page-layer--keep-hero');
    ghost.classList.add(back ? 'page-layer--leave-back' : 'page-layer--leave');
    if (keepHero) ghost.classList.add('page-layer--keep-hero');
    ghost.removeAttribute('data-key');
    viewport.appendChild(ghost);
    const done = () => ghost.remove();
    ghost.addEventListener('animationend', done, { once: true });
    window.setTimeout(done, 700); // fallback if animationend never fires
};

// Browser/router back & forward. This router commits the new route synchronously
// inside its OWN popstate handler (registered when the Router mounts), so to run
// first — while the old page is still on screen — we register here at module
// load, before render() creates the Router, in the capture phase. It works
// straight off the DOM at event time: clone the current layer into a ghost and
// arm the direction (by page keys, same as link clicks) for the upcoming mount.
if (typeof window !== 'undefined') {
    window.addEventListener(
        'popstate',
        () => {
            const viewport = document.querySelector<HTMLElement>('.page-viewport');
            const oldLayer = viewport?.querySelector<HTMLElement>(':scope > .page-layer[data-key]');
            if (!viewport || !oldLayer) return;

            const fromKey = oldLayer.dataset.key ?? '';
            // With the search: `?page=` is part of a paged list's page key.
            const toPath = window.location.pathname + window.location.search;
            // Same page (e.g. closing a lightbox photo that pushed a URL): skip.
            if (fromKey === pageKey(toPath)) return;

            // Lets a list page scroll back to the album we're leaving.
            recordPageChange(fromKey, true);

            const back = slidesBack(fromKey, toPath);
            const keepHero = sharesHero(fromKey, toPath);
            spawnGhost(viewport, oldLayer, back, keepHero);
            pendingSlide = back ? 'back' : 'forward';
            pendingKeepHero = keepHero;
        },
        { capture: true },
    );
}

// Push/carousel transition between top-level pages. Moving to a page further
// right in the navbar slides the outgoing page out to the left and the incoming
// page in from the right; moving left slides the opposite way.
//
// The route outlet is keyed on the page identity (see pageKey), so it remounts
// (and replays the slide) only on real page changes — not on lightbox photo
// open/close. The outgoing page slides out as a ghost (spawnGhost). Link-click
// navigation is caught by `useBeforeLeave`; back/forward by the module-level
// `popstate` handler above.
const AnimatedOutlet: Component<{ children?: JSX.Element }> = (props) => {
    const loc = useLocation();
    // Path plus search, so paging a list (`?page=`) is a page change too.
    const path = () => loc.pathname + loc.search;
    const key = () => pageKey(path());
    let viewport: HTMLDivElement | undefined;
    let live: HTMLDivElement | undefined;

    // Link-click / programmatic push navigation. popstate is handled separately
    // (module-level handler above), so ignore numeric `e.to` here.
    useBeforeLeave((e) => {
        if (!viewport || !live) return;
        if (typeof e.to === 'number') return; // back/forward handled by popstate
        // Redirect legs (`<Navigate>` uses `replace`). Don't spawn a second
        // ghost and don't touch the pending slide, so the real destination
        // inherits the one already in flight from the original click.
        if (e.options?.replace) return;

        const fromPath = path();
        const targetPath = String(e.to);

        // Same page — no visible change, so don't animate.
        if (pageKey(targetPath) === pageKey(fromPath)) return;

        const forced = (e.options?.state as { slide?: SlideDirection } | undefined)?.slide;
        recordPageChange(pageKey(fromPath), forced === 'back');
        const back = slidesBack(fromPath, targetPath, forced);
        const keepHero = sharesHero(fromPath, targetPath);
        spawnGhost(viewport, live, back, keepHero);
        pendingSlide = back ? 'back' : 'forward';
        pendingKeepHero = keepHero;
    });

    return (
        <div class="page-viewport" ref={viewport}>
            <Show when={key()} keyed>
                {(k) => {
                    // Runs once per mount; consume the pending slide.
                    const dir = pendingSlide;
                    const keepHero = pendingKeepHero;
                    pendingSlide = null;
                    pendingKeepHero = false;
                    const cls = dir
                        ? `page-layer ${dir === 'back' ? 'page-layer--enter-back' : 'page-layer--enter'}` +
                          (keepHero ? ' page-layer--keep-hero' : '')
                        : 'page-layer';
                    // A real navigation (not the initial mount): open at the top.
                    if (dir) scrollNewPageToTop(viewport);
                    return (
                        <div class={cls} ref={live} data-key={k}>
                            {props.children}
                        </div>
                    );
                }}
            </Show>
        </div>
    );
};

const RootLayout: Component<{ children?: JSX.Element }> = (props) => (
    <>
        <SearchBridge />
        <OffCanvasCloser />
        <PageViewTracker />
        <FullscreenPrompt />
        <AnimatedOutlet>{props.children}</AnimatedOutlet>
    </>
);

export const App: Component<{ bootstrap?: Bootstrap }> = (props) => {
    const isDrive = !!props.bootstrap?.site?.drive;
    const routes = routesFor(isDrive);
    // Keyed on refreshKey so returning to the app after a long absence remounts
    // the whole route tree (re-matching the current URL, re-fetching page data
    // against a cleared cache). The BootstrapProvider stays mounted outside it,
    // so its `consumed` flag survives and pages fetch fresh instead of being
    // re-seeded with the now-stale server bootstrap.
    return (
        <BootstrapProvider value={props.bootstrap}>
            <Show when={refreshKey()} keyed>
                {(_key) => <Router root={RootLayout}>{routes}</Router>}
            </Show>
        </BootstrapProvider>
    );
};
